import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CreateSubscriptions from './CreateSubscriptions';
import { newServer } from 'constants/constants';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Header } from 'components/Header/Header';

interface Subscription {
  subscribe_rate_id: number;
  marketing_description: string;
  hidden: boolean;
  marketing_title: string;
  fixed_value: number;
}

const DeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  subscription,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  subscription: Subscription | null;
}) => {
  if (!subscription) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>
        <Typography variant='body1'>
          Are you sure you want to delete the subscription "{subscription.marketing_title}"?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'text.primary', borderColor: 'divider' }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          sx={{
            color: 'error.contrastText',
            backgroundColor: 'error.main',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ParentComponent: React.FC = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);
  const [subscriptionsData, setSubscriptionsData] = useState<Subscription[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const onConfirm = () => {
    handleDeleteClick(selectedSub?.subscribe_rate_id || 0);
    handlePreviewClose();
  };
  const token = localStorage.getItem('token');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // Si quieres que el modal sea fullscreen en móviles

  const handleOpenDrawer = (sub: Subscription, edit: boolean) => {
    if (edit) {
      setSelectedSub(sub);
      setOpenDrawer(true);
    } else {
      setSelectedSub(null);
      setOpenDrawer(true);
    }
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    getSubscription();
  };

  const handlePreviewOpen = (sub: Subscription) => {
    setSelectedSub(sub);
    setOpenPreview(true);
  };

  const handlePreviewClose = () => {
    setOpenPreview(false);
    setSelectedSub(null);
    getSubscription();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Data segmentada para la paginación
  const paginatedData = subscriptionsData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getSubscription = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/all`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      setSubscriptionsData(data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const deleteSubscription = async (id: number) => {
    try {
      const response = await fetch(`${newServer}subscriptions/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      getSubscription();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteClick = (id: number) => {
    deleteSubscription(id);
  };

  useEffect(() => {
    getSubscription();
  }, []);

  return (
    <>
      <CreateSubscriptions
        open={openDrawer}
        selectedSub={selectedSub}
        onClose={handleCloseDrawer}
      />

      <DeleteConfirmationModal
        open={openPreview}
        onClose={handlePreviewClose}
        onConfirm={onConfirm}
        subscription={selectedSub}
      />

      <Header />

      <Box sx={{ p: 3, bgcolor: 'background.default', height: '100vh' }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom sx={{ color: 'text.primary' }}>
          Subscription Management
        </Typography>
        <Divider sx={{ mb: 3, borderColor: 'divider' }} />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
            onClick={() => {
              handleOpenDrawer(
                {
                  subscribe_rate_id: 0,
                  marketing_description: '',
                  hidden: false,
                  marketing_title: '',
                  fixed_value: 0,
                },
                false
              );
            }}
          >
            Create
          </Button>
        </Box>

        {/* Contenedor responsive para la tabla */}
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer
            component={Paper}
            sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', minWidth: 600 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    Subscriptions ID.
                  </TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    Subscriptions Name
                  </TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell align='right' sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((sub) => (
                  <TableRow
                    key={sub.subscribe_rate_id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: 'var(--surface-2)' },
                      '&:hover': { backgroundColor: 'var(--surface-3)' },
                    }}
                  >
                    <TableCell sx={{ color: 'text.primary' }}>{sub.subscribe_rate_id}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{sub.marketing_title}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>
                      {sub.hidden ? 'Active' : 'Inactive'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{sub.fixed_value}</TableCell>
                    <TableCell align='right'>
                      <Button
                        variant='outlined'
                        size='small'
                        sx={{
                          mr: 1,
                          borderColor: 'divider',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: 'text.primary',
                            color: 'background.paper',
                            borderColor: 'text.primary',
                          },
                        }}
                        onClick={() => handlePreviewOpen(sub)}
                      >
                        <DeleteIcon />
                      </Button>
                      <Button
                        variant='contained'
                        size='small'
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          mr: 1,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                        onClick={() => {
                          handleOpenDrawer(sub, true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ color: 'text.primary' }}>
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component='div'
              count={subscriptionsData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid', borderColor: 'divider', color: 'text.primary' }}
            />
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default ParentComponent;
